/**
 * This implementation of the SessionDataService API stores data locally either on disk when running within CEF or
 * in local storage when running in a browser.  It is the default implementation used by the UIContainer but UIContainer
 * allows a custom implementation to be substituted, for example to store data on the server in the browser case.
 */
define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/Deferred",
    "MW/uiframework/PreferenceDirectoryService",
    "MW/uiframework/uicontainer/SessionDataService"
], function (declare, lang, Deferred, PreferenceDirectoryService, SessionDataService) {

    /**
     * @class LocalSessionDataService
     */
    return declare([SessionDataService], {

        // Properties
        // _saveInfo stores the following for each applicable key
        //    minSaveInterval
        //    pendingSaveData
        //    waitingForTimeout

        constructor: function () {
            this._saveInfo = {};
            this._saveToPreferences = !!window.cefclient;
        },

        /**
         * Specifies a limit on how often data will be saved under the specified key.  If the save function is called
         * more frequently than the specified limit, then save operations will be coalesced.  If save is called for a
         * particular key without this method being called first then the save operation will be carried out immediately.
         *
         * @param key - the key for which data save frequency is being limited
         * @param milliseconds - the minimum interval between save operations in milliseconds
         */
        setMinimumSaveInterval: function (key, milliseconds) {
            var saveInfo = this._saveInfo[key];
            if (!saveInfo) {
                saveInfo = {};
                this._saveInfo[key] = saveInfo;
            }
            saveInfo.minSaveInterval = milliseconds;
        },

        save: function (key, data, immediate) {
            var that = this;
            if (immediate) {
                this._saveNow(key, data);
            } else {
                var saveInfo = this._saveInfo[key];
                if (!saveInfo) {
                    this._saveNow(key, data);
                } else {
                    saveInfo.pendingSaveData = data;
                    if (!saveInfo.waitingForTimeout) {
                        saveInfo.waitingForTimeout = true;
                        setTimeout(lang.hitch(this, function () {
                            if (saveInfo.pendingSaveData) {
                                that._saveNow(key, saveInfo.pendingSaveData);
                                saveInfo.waitingForTimeout = false;
                                saveInfo.pendingSaveData = null;
                            }
                        }), saveInfo.minSaveInterval);
                    }
                }
            }
        },

        flush: function () {
            Object.keys(this._saveInfo).forEach(function (key) {
                var saveInfo = this._saveInfo[key];
                if (saveInfo.pendingSaveData) {
                    this._saveNow(key, saveInfo.pendingSaveData);
                    saveInfo.waitingForTimeout = false;
                    saveInfo.pendingSaveData = null;
                }
            }, this);
        },

        retrieve: function (key) {
            var deferred = new Deferred();
            if (this._saveToPreferences) {
                PreferenceDirectoryService.readFileFromPreferenceDirectory(key + ".json").then(
                    function (result) {
                        deferred.resolve(JSON.parse(result));
                    },
                    function (error) {
                        deferred.reject(error);
                    }
                );
            } else {
                var dataString = localStorage.getItem(key);
                if (dataString) {
                    deferred.resolve(JSON.parse(dataString));
                } else {
                    deferred.reject(key + " not found in local storage");
                }
            }
            return deferred.promise;
        },

        _saveNow: function (key, data) {
            var dataString = JSON.stringify(data);
            if (this._saveToPreferences) {
                PreferenceDirectoryService.writeFileToPreferenceDirectory(key + ".json", dataString);
            } else {
                localStorage.setItem(key, dataString);
            }
        }
    });
});
